import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import {
  Calendar,
  Target,
  TrendingUp,
  User,
  Clock,
  Activity,
  ChevronRight,
  Download,
  Loader2,
  Zap,
  CheckCircle,
  Trophy,
  Plus,
  Send
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

// Interfaces for drill-based planning (from reports version)
interface DrillCard {
  id: string;
  name: string;
  description: string;
  duration: number;
  difficulty: 'easy' | 'medium' | 'hard';
  targetSkills: string[];
  equipment?: string[];
  keyPoints: string[];
}

// Interfaces for multi-week planning (from coaching version)
interface TrainingWeek {
  weekNumber: number;
  focus: string;
  drills: string[];
  milestones: string[];
  recovery: string;
}

interface TrainingPlan {
  weeks: TrainingWeek[];
  overallObjectives: string[];
  equipmentNeeded: string[];
  progressionMetrics: string[];
}

// Props interface supporting both use cases
interface TrainingPlanGeneratorProps {
  playerId?: string;
  playerName?: string;
  currentLevel?: 'beginner' | 'intermediate' | 'advanced' | 'professional';
  mode?: 'quick' | 'comprehensive';
}

// Skill options for visual selection (from reports version)
const skillOptions = [
  { id: 'batting', name: 'Batting Technique', icon: 'B' },
  { id: 'footwork', name: 'Footwork', icon: 'F' },
  { id: 'timing', name: 'Timing', icon: 'T' },
  { id: 'power', name: 'Power Generation', icon: 'P' },
  { id: 'placement', name: 'Shot Placement', icon: 'S' },
  { id: 'defense', name: 'Defensive Play', icon: 'D' },
  { id: 'spin', name: 'Playing Spin', icon: 'SP' },
  { id: 'pace', name: 'Playing Pace', icon: 'PC' },
  { id: 'fitness', name: 'Physical Fitness', icon: 'FT' },
  { id: 'mental', name: 'Mental Toughness', icon: 'M' },
];

// Drill templates (from reports version)
const drillTemplates: DrillCard[] = [
  {
    id: 'drill-1',
    name: 'Shadow Batting',
    description: 'Practice batting technique without a ball',
    duration: 10,
    difficulty: 'easy',
    targetSkills: ['batting', 'footwork'],
    keyPoints: ['Focus on stance', 'Smooth bat swing', 'Follow through'],
  },
  {
    id: 'drill-2',
    name: 'Wall Ball Practice',
    description: 'Hit tennis ball against wall for timing',
    duration: 15,
    difficulty: 'medium',
    targetSkills: ['timing', 'reflexes'],
    equipment: ['Tennis ball', 'Wall'],
    keyPoints: ['Quick reactions', 'Shot selection', 'Timing'],
  },
  {
    id: 'drill-3',
    name: 'Power Hitting Drills',
    description: 'Focus on generating maximum power',
    duration: 20,
    difficulty: 'hard',
    targetSkills: ['power', 'batting'],
    equipment: ['Cricket balls', 'Bowling machine'],
    keyPoints: ['Weight transfer', 'Hip rotation', 'Follow through'],
  },
];

export function TrainingPlanGenerator({
  playerId,
  playerName: initialPlayerName,
  currentLevel = 'intermediate',
  mode = 'comprehensive'
}: TrainingPlanGeneratorProps = {}) {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);
  const [trainingPlan, setTrainingPlan] = useState<TrainingPlan | null>(null);
  const [activeTab, setActiveTab] = useState(mode === 'quick' ? 'configure' : 'profile');

  // Player profile state (coaching version)
  const [playerProfile, setPlayerProfile] = useState({
    name: initialPlayerName || '',
    age: '',
    skillLevel: currentLevel,
    playingStyle: 'all-rounder',
    strengths: '',
    weaknesses: ''
  });

  // Quick plan settings (reports version)
  const [planDuration, setPlanDuration] = useState(4); // weeks
  const [sessionsPerWeek, setSessionsPerWeek] = useState(3);
  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState<'beginner' | 'intermediate' | 'advanced'>(currentLevel === 'professional' ? 'advanced' : currentLevel as any);
  const [includeVideoLinks, setIncludeVideoLinks] = useState(true);
  const [includeEquipmentList, setIncludeEquipmentList] = useState(true);

  // Goals state
  const [goals, setGoals] = useState<string[]>(['']);

  // Update player name when prop changes
  useEffect(() => {
    if (initialPlayerName) {
      setPlayerProfile(prev => ({ ...prev, name: initialPlayerName }));
    }
  }, [initialPlayerName]);

  const handleSkillToggle = (skillId: string) => {
    setSelectedSkills(prev =>
      prev.includes(skillId)
        ? prev.filter(id => id !== skillId)
        : [...prev, skillId]
    );
  };

  const handleGeneratePlan = async () => {
    // Validate based on mode
    if (mode === 'quick' && selectedSkills.length === 0) {
      toast({
        title: 'Select Skills',
        description: 'Please select at least one skill to focus on',
        variant: 'destructive',
      });
      return;
    }

    if (mode === 'comprehensive' && !playerProfile.name) {
      toast({
        title: 'Missing Information',
        description: 'Please enter player name',
        variant: 'destructive'
      });
      return;
    }

    setIsGenerating(true);

    try {
      const response = await fetch('http://localhost:3001/training/plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          playerId,
          playerProfile: {
            ...playerProfile,
            strengths: playerProfile.strengths.split(',').map(s => s.trim()).filter(Boolean),
            weaknesses: playerProfile.weaknesses.split(',').map(s => s.trim()).filter(Boolean)
          },
          duration: planDuration,
          sessionsPerWeek,
          focus: selectedSkills,
          difficulty,
          goals: goals.filter(Boolean),
          includeVideoLinks,
          includeEquipmentList
        })
      });

      if (!response.ok) {
        throw new Error('Failed to generate training plan');
      }

      const plan = await response.json();
      setTrainingPlan(plan);
      setActiveTab('preview');

      toast({
        title: 'Training Plan Generated',
        description: 'Your personalized training plan is ready'
      });
    } catch (error) {
      console.error('Error generating training plan:', error);
      toast({
        title: 'Generation Failed',
        description: 'Failed to generate training plan. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const addGoal = () => {
    setGoals([...goals, '']);
  };

  const updateGoal = (index: number, value: string) => {
    const newGoals = [...goals];
    newGoals[index] = value;
    setGoals(newGoals);
  };

  const removeGoal = (index: number) => {
    setGoals(goals.filter((_, i) => i !== index));
  };

  const exportPlan = () => {
    if (!trainingPlan) return;

    const planText = JSON.stringify(trainingPlan, null, 2);
    const blob = new Blob([planText], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `training-plan-${playerProfile.name || 'player'}-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSendPlan = () => {
    toast({
      title: 'Sending Plan',
      description: 'Your training plan has been sent',
    });
  };

  // Render quick configuration (from reports version)
  const renderQuickConfig = () => (
    <div className="space-y-6">
      {/* Plan Duration */}
      <div className="space-y-4">
        <div>
          <Label>Plan Duration</Label>
          <p className="text-sm text-muted-foreground">
            {planDuration} weeks ({planDuration * sessionsPerWeek} total sessions)
          </p>
        </div>
        <Slider
          value={[planDuration]}
          onValueChange={([value]) => setPlanDuration(value)}
          min={1}
          max={12}
          step={1}
          className="w-full"
        />
      </div>

      {/* Sessions per Week */}
      <div className="space-y-4">
        <div>
          <Label>Sessions per Week</Label>
          <p className="text-sm text-muted-foreground">
            {sessionsPerWeek} sessions
          </p>
        </div>
        <Slider
          value={[sessionsPerWeek]}
          onValueChange={([value]) => setSessionsPerWeek(value)}
          min={1}
          max={7}
          step={1}
          className="w-full"
        />
      </div>

      {/* Difficulty Level */}
      <div className="space-y-2">
        <Label>Difficulty Level</Label>
        <Select value={difficulty} onValueChange={(value: any) => setDifficulty(value)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="beginner">Beginner</SelectItem>
            <SelectItem value="intermediate">Intermediate</SelectItem>
            <SelectItem value="advanced">Advanced</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Skill Selection */}
      <div className="space-y-4">
        <Label>Focus Areas</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {skillOptions.map((skill) => (
            <div
              key={skill.id}
              className={`
                p-3 rounded-lg border-2 cursor-pointer transition-all
                ${selectedSkills.includes(skill.id)
                  ? 'border-primary bg-primary/5'
                  : 'border-gray-200 hover:border-gray-300'
                }
              `}
              onClick={() => handleSkillToggle(skill.id)}
            >
              <div className="flex items-center space-x-2">
                <span className="text-lg font-bold text-primary">{skill.icon}</span>
                <span className="text-sm font-medium">{skill.name}</span>
                {selectedSkills.includes(skill.id) && (
                  <CheckCircle className="w-4 h-4 text-primary ml-auto" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Additional Options */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Include Video Links</Label>
            <p className="text-sm text-muted-foreground">
              Add instructional video links for drills
            </p>
          </div>
          <Switch
            checked={includeVideoLinks}
            onCheckedChange={setIncludeVideoLinks}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Include Equipment List</Label>
            <p className="text-sm text-muted-foreground">
              List all required equipment upfront
            </p>
          </div>
          <Switch
            checked={includeEquipmentList}
            onCheckedChange={setIncludeEquipmentList}
          />
        </div>
      </div>

      {/* Generate Button */}
      <Button
        className="w-full"
        size="lg"
        onClick={handleGeneratePlan}
        disabled={isGenerating || selectedSkills.length === 0}
      >
        {isGenerating ? (
          <>Generating Plan...</>
        ) : (
          <>
            <Zap className="w-4 h-4 mr-2" />
            Generate Training Plan
          </>
        )}
      </Button>
    </div>
  );

  // Render player profile form (from coaching version)
  const renderPlayerProfile = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Player Profile
        </CardTitle>
        <CardDescription>
          Enter player information to generate a personalized training plan
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">Player Name</Label>
            <Input
              id="name"
              value={playerProfile.name}
              onChange={(e) => setPlayerProfile({...playerProfile, name: e.target.value})}
              placeholder="Enter player name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="age">Age</Label>
            <Input
              id="age"
              type="number"
              value={playerProfile.age}
              onChange={(e) => setPlayerProfile({...playerProfile, age: e.target.value})}
              placeholder="Enter age"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="skillLevel">Skill Level</Label>
            <Select
              value={playerProfile.skillLevel}
              onValueChange={(value) => setPlayerProfile({...playerProfile, skillLevel: value as 'beginner' | 'intermediate' | 'advanced' | 'professional'})}
            >
              <SelectTrigger id="skillLevel">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
                <SelectItem value="professional">Professional</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="playingStyle">Playing Style</Label>
            <Select
              value={playerProfile.playingStyle}
              onValueChange={(value) => setPlayerProfile({...playerProfile, playingStyle: value})}
            >
              <SelectTrigger id="playingStyle">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="aggressive">Aggressive</SelectItem>
                <SelectItem value="defensive">Defensive</SelectItem>
                <SelectItem value="all-rounder">All-rounder</SelectItem>
                <SelectItem value="technical">Technical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="strengths">Strengths (comma-separated)</Label>
          <Input
            id="strengths"
            value={playerProfile.strengths}
            onChange={(e) => setPlayerProfile({...playerProfile, strengths: e.target.value})}
            placeholder="e.g., Good timing, Strong pull shot, Quick footwork"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="weaknesses">Areas for Improvement (comma-separated)</Label>
          <Input
            id="weaknesses"
            value={playerProfile.weaknesses}
            onChange={(e) => setPlayerProfile({...playerProfile, weaknesses: e.target.value})}
            placeholder="e.g., Spin bowling, Short ball, Shot selection"
          />
        </div>
      </CardContent>
    </Card>
  );

  // Render training goals (from coaching version)
  const renderGoals = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Training Goals
        </CardTitle>
        <CardDescription>
          Define specific goals for the training program
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {goals.map((goal, index) => (
          <div key={index} className="flex gap-2">
            <Input
              value={goal}
              onChange={(e) => updateGoal(index, e.target.value)}
              placeholder={`Goal ${index + 1}: e.g., Improve cover drive consistency`}
            />
            {goals.length > 1 && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => removeGoal(index)}
              >
                x
              </Button>
            )}
          </div>
        ))}
        <Button variant="outline" onClick={addGoal} className="w-full">
          Add Another Goal
        </Button>
      </CardContent>
    </Card>
  );

  // Render plan preview (combined from both versions)
  const renderPlanPreview = () => (
    <>
      {trainingPlan && (
        <>
          {/* Plan Overview (reports version style) */}
          <Card>
            <CardHeader>
              <CardTitle>Plan Overview</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{planDuration}</p>
                  <p className="text-sm text-muted-foreground">Weeks</p>
                </div>
                <div className="text-center">
                  <Activity className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{planDuration * sessionsPerWeek}</p>
                  <p className="text-sm text-muted-foreground">Sessions</p>
                </div>
                <div className="text-center">
                  <Clock className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">60</p>
                  <p className="text-sm text-muted-foreground">Min/Session</p>
                </div>
                <div className="text-center">
                  <Trophy className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold">{selectedSkills.length || trainingPlan.overallObjectives.length}</p>
                  <p className="text-sm text-muted-foreground">Focus Areas</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Weekly Schedule (coaching version style) */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  {trainingPlan.weeks.length}-Week Training Plan
                </CardTitle>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={exportPlan}>
                    <Download className="h-4 w-4 mr-1" />
                    Export
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleSendPlan}>
                    <Send className="h-4 w-4 mr-1" />
                    Send
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="weekly">Weekly Plan</TabsTrigger>
                  <TabsTrigger value="equipment">Equipment</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-2">Overall Objectives</h4>
                    <div className="flex flex-wrap gap-2">
                      {trainingPlan.overallObjectives.map((objective, i) => (
                        <Badge key={i} variant="secondary">{objective}</Badge>
                      ))}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-sm font-medium mb-2">Progression Metrics</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {trainingPlan.progressionMetrics.map((metric, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <Activity className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">{metric}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="weekly">
                  <ScrollArea className="h-96 pr-4">
                    <div className="space-y-4">
                      {trainingPlan.weeks.map((week) => (
                        <Card key={week.weekNumber}>
                          <CardHeader>
                            <CardTitle className="text-base">
                              Week {week.weekNumber}: {week.focus}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            <div>
                              <h5 className="text-sm font-medium mb-1">Drills</h5>
                              <ul className="text-sm text-muted-foreground space-y-1">
                                {week.drills.map((drill, i) => (
                                  <li key={i} className="flex items-start gap-2">
                                    <ChevronRight className="h-3 w-3 mt-0.5 flex-shrink-0" />
                                    {drill}
                                  </li>
                                ))}
                              </ul>
                            </div>

                            <div>
                              <h5 className="text-sm font-medium mb-1">Milestones</h5>
                              <div className="flex flex-wrap gap-1">
                                {week.milestones.map((milestone, i) => (
                                  <Badge key={i} variant="outline" className="text-xs">
                                    {milestone}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            <div>
                              <h5 className="text-sm font-medium mb-1">Recovery</h5>
                              <p className="text-sm text-muted-foreground">{week.recovery}</p>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="equipment" className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium mb-3">Required Equipment</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {trainingPlan.equipmentNeeded.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 border rounded-lg">
                          <div className="h-2 w-2 bg-primary rounded-full" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </>
  );

  // Render drill library (from reports version)
  const renderDrillLibrary = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">
          Browse and customize drill exercises
        </p>
        <Button size="sm">
          <Plus className="w-4 h-4 mr-2" />
          Add Custom Drill
        </Button>
      </div>

      <div className="grid gap-4">
        {drillTemplates.map((drill) => (
          <Card key={drill.id}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">{drill.name}</CardTitle>
                <div className="flex items-center space-x-2">
                  <Badge variant={
                    drill.difficulty === 'easy' ? 'secondary' :
                    drill.difficulty === 'medium' ? 'outline' : 'default'
                  }>
                    {drill.difficulty}
                  </Badge>
                  <Badge variant="outline">
                    {drill.duration} min
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-3">
                {drill.description}
              </p>
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs font-medium">Skills:</span>
                  {drill.targetSkills.map((skill) => (
                    <Badge key={skill} variant="secondary" className="text-xs">
                      {skill}
                    </Badge>
                  ))}
                </div>
                {drill.equipment && (
                  <div className="flex flex-wrap gap-2">
                    <span className="text-xs font-medium">Equipment:</span>
                    {drill.equipment.map((item) => (
                      <span key={item} className="text-xs text-muted-foreground">
                        {item}
                      </span>
                    ))}
                  </div>
                )}
                <div className="pt-2">
                  <span className="text-xs font-medium">Key Points:</span>
                  <ul className="text-xs text-muted-foreground mt-1">
                    {drill.keyPoints.map((point, idx) => (
                      <li key={idx}>- {point}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Quick mode UI (reports version style)
  if (mode === 'quick') {
    return (
      <div className="max-w-6xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Training Plan Generator</span>
              <div className="flex items-center space-x-2">
                <Badge variant="outline">{playerProfile.name || 'Player'}</Badge>
                <Badge variant="secondary">{currentLevel}</Badge>
              </div>
            </CardTitle>
            <CardDescription>
              Create a personalized training plan with drills and exercises
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="configure">Configure</TabsTrigger>
                <TabsTrigger value="preview" disabled={!trainingPlan}>Preview</TabsTrigger>
                <TabsTrigger value="drills">Drill Library</TabsTrigger>
              </TabsList>

              <TabsContent value="configure" className="space-y-6 mt-6">
                {renderQuickConfig()}
              </TabsContent>

              <TabsContent value="preview" className="space-y-6 mt-6">
                {renderPlanPreview()}
              </TabsContent>

              <TabsContent value="drills" className="space-y-4 mt-6">
                {renderDrillLibrary()}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Comprehensive mode UI (coaching version style)
  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="profile">Profile</TabsTrigger>
          <TabsTrigger value="goals">Goals</TabsTrigger>
          <TabsTrigger value="preview" disabled={!trainingPlan}>Plan</TabsTrigger>
          <TabsTrigger value="drills">Drills</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-6 mt-6">
          {renderPlayerProfile()}
          {renderQuickConfig()}
        </TabsContent>

        <TabsContent value="goals" className="space-y-6 mt-6">
          {renderGoals()}

          {/* Generate Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleGeneratePlan}
              disabled={isGenerating}
              size="lg"
              className="w-full max-w-md"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Generating Training Plan...
                </>
              ) : (
                <>
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Generate Training Plan
                </>
              )}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="space-y-6 mt-6">
          {renderPlanPreview()}
        </TabsContent>

        <TabsContent value="drills" className="space-y-4 mt-6">
          {renderDrillLibrary()}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Default export for compatibility with reports version import
export default TrainingPlanGenerator;
